import React, { useState } from "react";
import { toast } from "react-hot-toast";

const AddNailsModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [nailType, setNailType] = useState("handmade"); // 'handmade' or 'machine'

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const form = e.target;
    const formData = new FormData(form);
    
    // Add the selected category to the formData
    formData.append("category", nailType);

    try {
      // Use standard fetch since we're uploading FormData (files)
      const response = await fetch("https://wittynailtip.com/backend/admin-upload-nails.php", {
        method: "POST",
        credentials: "include", 
        body: formData,
      });

      const result = await response.json();
      if (result.success) {
        toast.success(result.message || "Nails added successfully!");
        onSuccess();
        onClose();
      } else {
        toast.error(result.message || "Failed to add nails.");
        if (result.debug) console.log("PHP Debug Info:", result.debug);
      }
    } catch (error) {
      toast.error("An error occurred during upload.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm font-jost">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
        
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-white border-b border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900">Add New Nails</h2>
          <button onClick={onClose} className="p-2 text-gray-400 transition-colors hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          
          {/* Nail Type Selector */}
          <div className="p-5 bg-[#fff1f5] rounded-2xl border border-[#FFB7CF]/30">
            <label className="block mb-3 text-sm font-bold text-gray-900">What type of nails are these?</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="nailTypeSelector" value="handmade" checked={nailType === "handmade"} onChange={(e) => setNailType(e.target.value)} className="w-5 h-5 text-[#FFB7CF] focus:ring-[#FFB7CF] border-gray-300" />
                <span className="font-medium text-gray-700">Handmade Nails</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="nailTypeSelector" value="machine" checked={nailType === "machine"} onChange={(e) => setNailType(e.target.value)} className="w-5 h-5 text-[#FFB7CF] focus:ring-[#FFB7CF] border-gray-300" />
                <span className="font-medium text-gray-700">Mechanical Nails</span>
              </label>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              {nailType === "handmade" ? "Handmade nails require quantities for each size (XS, S, M, L)." : "Mechanical nails require quantities for each color variant."}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700">Product Title *</label>
              <input type="text" name="title" required placeholder="Enter product title" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FFB7CF] focus:border-transparent outline-none transition-all" />
            </div>

            <div className="space-y-2 md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700">Description *</label>
              <textarea name="description" required rows="4" placeholder="Enter product description" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FFB7CF] focus:border-transparent outline-none transition-all"></textarea>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Price (USD) *</label>
              <input type="number" name="price" step="0.01" min="0" required placeholder="0.00" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FFB7CF] focus:border-transparent outline-none transition-all" />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Main Product Image *</label>
              <input type="file" name="image" accept="image/*" required className="w-full px-4 py-2 border border-gray-200 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#fff1f5] file:text-[#FFB7CF] hover:file:bg-[#ffe1ea] rounded-xl outline-none" />
            </div>
          </div>

          {/* Additional Images */}
          <div className="pt-6 border-t border-gray-100">
            <h3 className="mb-4 text-lg font-bold text-gray-900">Additional Images (Optional)</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[2, 3, 4, 5].map((num) => (
                <div key={num} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-600">Extra Image {num}</label>
                  <input type="file" name={`pimg${num}`} accept="image/*" className="w-full text-sm text-gray-500 file:mr-2 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-medium file:bg-gray-100 file:text-gray-700" />
                </div>
              ))}
            </div>
          </div>

          {/* Size Quantities (Handmade Only) */}
          {nailType === "handmade" && (
            <div className="pt-6 border-t border-gray-100">
              <h3 className="mb-4 text-lg font-bold text-gray-900">Size Quantities *</h3>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                {["xs", "small", "med", "large"].map((size) => (
                  <div key={size} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 uppercase">{size === 'small' ? 'S' : size === 'med' ? 'M' : size === 'large' ? 'L' : 'XS'}</label>
                    <input type="number" name={size} required min="0" defaultValue="0" className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FFB7CF] outline-none" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Colors */}
          <div className="pt-6 border-t border-gray-100">
            <h3 className="mb-4 text-lg font-bold text-gray-900">Colors (Up to 10)</h3>
            <p className="mb-6 text-sm text-gray-500">Add the available colors. You can skip the ones you don't need.</p>
            
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <div key={num} className="p-4 border border-gray-100 bg-gray-50/50 rounded-2xl">
                  <h4 className="mb-3 font-semibold text-gray-700">Color Variant {num}</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block mb-1 text-xs font-medium text-gray-500">Color Name</label>
                      <select name={`color${num}`} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-[#FFB7CF] bg-white">
                        <option value="">Select a color (or skip)</option>
                        <option value="Pink">Pink</option>
                        <option value="Red">Red</option>
                        <option value="Blue">Blue</option>
                        <option value="Yellow">Yellow</option>
                        <option value="Brown">Brown</option>
                        <option value="Nude">Nude</option>
                        <option value="White">White</option>
                        <option value="Black">Black</option>
                        <option value="Orange">Orange</option>
                        <option value="Purple">Purple</option>
                        <option value="Green">Green</option>
                        <option value="Wine">Wine</option>
                        <option value="Others">Others</option>
                        <option value="None">None</option>
                      </select>
                    </div>
                    <div>
                      <label className="block mb-1 text-xs font-medium text-gray-500">Color Image</label>
                      <input type="file" name={`image${num}`} accept="image/*" className="w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:bg-white file:border file:border-gray-200" />
                    </div>
                    {nailType === "machine" && (
                      <div>
                        <label className="block mb-1 text-xs font-medium text-gray-500">Quantity Available</label>
                        <input type="number" name={`q${num}`} min="0" placeholder="0" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-[#FFB7CF]" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-6 py-2.5 font-semibold text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="px-8 py-2.5 font-semibold text-white bg-[#FFB7CF] rounded-full hover:bg-[#ff9cbb] transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2">
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                  Uploading...
                </>
              ) : (
                "Save Nails"
              )}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default AddNailsModal;
